import { FormBase } from "../util/form-base-form";
import { InitListener } from "./init-listener";

type InitEntry = {
    id: string;
    selector: string;
    init: (el: Element) => void | Promise<void>;
    when?: (el: Element) => boolean;
};

class InitSetupFormBase extends FormBase {
    public getTargets(root: Document | Element, selector: string): Element[] {
        return this.queryElements(root, selector, Element);
    }
}

const initSetupFormBase = new InitSetupFormBase();

export class InitRegistry {
    private readonly _entries: InitEntry[];

    private readonly _ran: WeakMap<Element, Set<string>>;

    private _started: boolean;

    public constructor() {
        this._entries = this._createEntries();
        this._ran = this._createRanMap();

        this._started = false;
    }

    private _createEntries(): InitEntry[] {
        return [];
    }

    private _createRanMap(): WeakMap<Element, Set<string>> {
        return new WeakMap();
    }

    public register(entry: InitEntry): void {
        if (
            !entry ||
            typeof entry.id !== "string" ||
            typeof entry.selector !== "string" ||
            typeof entry.init !== "function"
        ) {
            throw new Error("[InitRegistry] Invalid entry");
        }

        if (this._entries.some((e) => e.id === entry.id)) {
            throw new Error(`[InitRegistry] Duplicate entry id: ${entry.id}`);
        }

        this._entries.push(entry);
    }

    public start(): void {
        if (this._started) return;
        this._started = true;

        InitListener.bindContentInserted((event) => {
            const root = this._extractInsertedRoot(event);
            if (root instanceof Element) {
                this.scan(root);
            }
        });
    }

    private _extractInsertedRoot(event: Event): Element | null {
        if (!(event instanceof CustomEvent)) {
            return null;
        }
        const detail = event.detail;
        if (!detail || typeof detail !== "object") {
            return null;
        }
        const root = Reflect.get(detail, "root");
        return root instanceof Element ? root : null;
    }

    private _hasRun(el: Element, id: string): boolean {
        const set = this._ran.get(el);
        return set ? set.has(id) : false;
    }

    private _markRun(el: Element, id: string): void {
        const existing = this._ran.get(el);
        if (existing) {
            existing.add(id);
            return;
        }
        this._ran.set(el, new Set([id]));
    }

    public scan(root: Document | Element = document): void {
        const container = root;

        for (const entry of this._entries) {
            const targets = this._collectTargets(container, entry.selector);

            for (const el of targets) {
                if (this._hasRun(el, entry.id)) continue;
                if (entry.when && !entry.when(el)) continue;

                this._markRun(el, entry.id);

                try {
                    const initReturn = entry.init(el);
                    Promise.resolve(initReturn).catch((error) => {
                        console.error(`[InitRegistry] Init failed: ${entry.id}`, error);
                    });
                } catch (error) {
                    console.error(`[InitRegistry] Init failed: ${entry.id}`, error);
                }
            }
        }
    }

    private _collectTargets(root: Document | Element, selector: string): Element[] {
        const targets: Element[] = [];
        if (root instanceof Element && root.matches(selector)) {
            targets.push(root);
        }
        targets.push(...initSetupFormBase.getTargets(root, selector));
        return targets;
    }
}

export const initRegistry = new InitRegistry();
